import React from 'react'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'

import { packageViewMobileStepperAndArrows } from '../../../../helpers/mobileSteppers'
import { dummyBox, validEmail } from '../../../../helpers/globalHelpers'
import { emailTextField } from '../../../../helpers/textfields'
import { standardButton } from '../../../../helpers/buttons'
import { standardErrorContainer } from '../../../../helpers/errors'
import { positionChangeWidthSm, showPayments, paymentAmount } from '../../../../helpers/variableDefaults'
import { standardSpinner } from '../../../../helpers/spinners'
import { userIsAuthenticated, getEmailFromLocalStorage } from '../../../../helpers/storage'

const sectionBoxSx = {
  my: 1,
  p: 2,
  backgroundColor: 'whitesmoke',
  width: '100%',
  boxShadow: 3,
  borderRadius: 3,
  display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center',
}

const sectionTitleSx = {
  fontWeight: 'bold',
  fontSize: 18,
  mb: 1,
}

const sectionInnerBoxSx = {
  width: '100%',
  p: 2,
  backgroundColor: 'white',
  borderRadius: '5px',
  border: '1px solid #e0e0e0',
  display: 'flex', flexDirection: 'column',
}

// Email To
export const emailTo = (send, setSend, handleSetSend, handleSendPresent, sendErrors, backArrow, forwardArrow, width, loading, setLoading, loginOptionChosen, setLoginOptionChosen, PaymentElement, handlePaymentElementChange, paymentMethodIsValid, paymentErrors, handleBackToReview, processingPayment) => {

  if (userIsAuthenticated() && loginOptionChosen === false) {
    send.senderEmail = getEmailFromLocalStorage()
    setLoginOptionChosen(true)
  }

  const senderEmailValid = validEmail(send.senderEmail)
  const buttonLabel = showPayments ? (processingPayment ? 'Processing Payment...' : 'Pay and Send Gift') : 'Send'
  const buttonDisabled = processingPayment || (showPayments
    ? (!senderEmailValid || !paymentMethodIsValid)
    : !senderEmailValid)

  return (
    <>
      {
        loading ?
          standardSpinner()
          :
          <>
            {/* Dummy Box to get spacing right */}
            {dummyBox('0px')}

            {/* Send Options */}
            <Box sx={{
              width: '100%',
              maxWidth: '380px',
              px: 2,
              pt: 2, pb: 2,
              display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'stretch',
            }}>

              {/* Email link to section */}
              <Box sx={sectionBoxSx}>
                <Typography sx={sectionTitleSx}>
                  Email link to
                </Typography>
                <Box sx={sectionInnerBoxSx}>
                  {emailTextField(
                    'senderEmail',
                    send.senderEmail,
                    'My email address (required)',
                    'standard',
                    handleSetSend,
                    send,
                    setSend,
                    width
                  )}
                  {emailTextField(
                    'recipientEmail',
                    send.recipientEmail,
                    'Recipient email address (optional)',
                    'standard',
                    handleSetSend,
                    send,
                    setSend,
                    width
                  )}
                </Box>
              </Box>

              {/* Summary + Payment Method (only when showPayments is true) */}
              {showPayments && PaymentElement && (
                <>
                  {/* Summary */}
                  {(() => {
                    const totalCharge = (paymentAmount + 0.3) / (1 - 0.029)
                    const processingFees = totalCharge - paymentAmount
                    return (
                      <Box sx={sectionBoxSx}>
                        <Typography sx={sectionTitleSx}>
                          Summary
                        </Typography>
                        <Box sx={sectionInnerBoxSx}>
                          <Box sx={{
                            width: '100%',
                            display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
                          }}>
                            <Typography>3D Wrapped Gift</Typography>
                            <Typography>{`$${paymentAmount.toFixed(2)}`}</Typography>
                          </Box>
                          <Box sx={{
                            width: '100%',
                            display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
                          }}>
                            <Typography>Processing Fees</Typography>
                            <Typography>{`$${processingFees.toFixed(2)}`}</Typography>
                          </Box>
                          <Box sx={{
                            width: '100%',
                            pt: .7,
                            display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
                          }}>
                            <Typography sx={{ fontWeight: 'bold' }}>TOTAL</Typography>
                            <Typography sx={{ fontWeight: 'bold' }}>{`$${totalCharge.toFixed(2)}`}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    )
                  })()}

                  {/* Payment Method */}
                  <Box sx={sectionBoxSx}>
                    <Typography sx={sectionTitleSx}>
                      Payment Method
                    </Typography>
                    <Box sx={sectionInnerBoxSx}>
                      <PaymentElement onChange={handlePaymentElementChange} />
                    </Box>
                  </Box>
                </>
              )}

              {/* Pay/Send + Back to Review buttons */}
              <Box
                sx={{
                  width: '100%',
                  mt: 1,
                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center',
                }}
              >
                {standardButton(
                  buttonLabel,
                  'button',
                  'contained',
                  buttonDisabled,
                  'secondary',
                  2,
                  1,
                  0,
                  '180px',
                  '45px',
                  handleSendPresent
                )}

                {showPayments && paymentMethodIsValid && !senderEmailValid && (
                  <Typography
                    sx={{
                      mb: 1,
                      fontStyle: 'italic',
                      fontSize: 14,
                      color: 'red',
                      textAlign: 'center',
                    }}
                  >
                    *Must provide email address
                  </Typography>
                )}

                {width >= positionChangeWidthSm && (
                  <Button
                    type='button'
                    variant='outlined'
                    color='primary'
                    onClick={handleBackToReview}
                    sx={{
                      mt: 0, mb: 2, mx: 0,
                      width: '180px', height: '45px',
                      borderWidth: 2,
                      '&:hover': { borderWidth: 2 },
                    }}
                  >
                    Back to Review
                  </Button>
                )}
              </Box>

              {/* Payment processing error */}
              {showPayments && paymentErrors && paymentErrors.length > 0 &&
                standardErrorContainer(
                  paymentErrors,
                  0,
                  0
                )
              }

              {/* Errors if there are errors */}
              {sendErrors &&
                standardErrorContainer(
                  'Could not send gift. Check your Internet connection.',
                  0,
                  0
                )
              }

            </Box>

            {/* Arrows in view if width under 600px */}
            {width < positionChangeWidthSm &&
              packageViewMobileStepperAndArrows(width, 7, backArrow, forwardArrow, false)
            }
          </>
      }
    </>
  )
}
